import {
    type Authentication,
    type AuthenticationManager,
    AuthorizationDecision,
    DefaultAuthorizationManager,
    type ServerAuthenticationEntryPoint,
    type ServerAuthenticationSuccessHandler
} from './types.ts';
import headers from './http-headers.ts';
import httpBasic from './http-basic.ts';
import resourceServer from './oauth2-resource-server.ts';
import serverBearerTokenAuthenticationConverter from './oauth2/token-converter.ts';
import bearerTokenServerAuthenticationEntryPoint from './oauth2/token-entry-point.ts';
import { BadJwtError, type Jwt, JwtError } from './oauth2/jwt-auth-manager.ts';
import { jwtVerifier, JwtVerifyError } from '@interopio/gateway/jose/jwt';
import {
    anyExchange,
    match,
    NO_MATCH,
    type ServerWebExchangeMatcher,
    type ServerWebExchangeMatcherEntry
} from '../util/matchers.ts';
import { errorFilter } from './error-filter.ts';
import { delegatingEntryPoint } from './delegating-entry-point.ts';
import delegatingAuthorizationManager from './delegating-authorization-manager.ts';
import authorizationFilter from './authorization-filter.ts';
import { exchangeFilter } from './exchange-filter.ts';
import type { SecurityContext } from './security-context.ts';
import cors from '../cors.ts';
import { type AsyncLocalStorage } from 'node:async_hooks';
import type { ServerWebExchange } from '../../../types/web/server';
import x509 from './x509.ts';
import { subjectX500PrincipalExtractor } from './preauth/x509.ts';
import { userDetailsServiceAuthenticationManager } from './users.ts';
import type { UserDetailsPasswordService, UserDetailsService } from './users/types.ts';
import type { CorsConfigSource } from '../../web/cors/types.ts';
import type { WebFilter } from '../../web/server/types.ts';
import { MatcherSecurityWebFilterChain, type SecurityWebFilterChain } from './web.ts';

export type HttpSecurityConfig = {
    authorize?: Array<[
            'any-exchange' | ServerWebExchangeMatcher,
        ( { access: 'permitted' | 'denied' | 'authenticated' }
            | { access: 'has-any-authority', authorities: string[] }
            | { access: 'has-ip-address', address: string })
    ]>,
    headers?: {
        disabled?: boolean
        xss?: { disabled?: boolean }
    },
    x509?: {
        disabled?: boolean
        /** Extract principal from Subject Alternative Name. Use 'email' to extract from email SAN. If undefined, uses subject (default). */
        principalAltName?: 'email'
    }
    cors?: {
        disabled?: boolean
    }
    basic?: {
        disabled?: boolean
        realm?: string
    },
    jwt?: {
        disabled?: boolean;
        issuerUri?: string; // base URI for the issuer, e.g. 'https://example.com'
        issuer?: string;
        audience?: string | string[]; // audience for the JWT, e.g. 'https://example.com/api'
    }
}

const filterOrder = {
    first: Number.MAX_SAFE_INTEGER,
    http_headers: 1 * 100,
    https_redirect: 2 * 100,
    cors: 3 * 100,
    http_basic: 6 * 100,
    authentication: 8 * 100,
    security_context_server_web_exchange: 15 * 100,
    error_translation: 18 * 100,
    authorization: 19 * 100,
    last: Number.MAX_SAFE_INTEGER
}
const filterOrderSymbol = Symbol.for("filterOrder");

export default (config: HttpSecurityConfig,
                context: {
                    storage: AsyncLocalStorage<{ securityContext?: Promise<SecurityContext> }>
                    corsConfigSource?: CorsConfigSource,
                    userDetailsService?: UserDetailsService,
                    userDetailsPasswordService?: UserDetailsPasswordService,
                    authenticationManager?: AuthenticationManager,
                }): SecurityWebFilterChain => {

    const getService = <T>(name: string, defaultService?: T): T => {
        if (context === undefined) {
            return defaultService as T; // will not throw but return defaultService which may be undefined
        }
        if (name === 'UserDetailsService' && context.userDetailsService !== undefined) {
            return context.userDetailsService as T;
        }
        if (name === 'AuthenticationManager' && context.authenticationManager !== undefined) {
            return context.authenticationManager as T;
        }
        if (defaultService !== undefined) {
            return defaultService;
        }
        throw new Error(`No service registered with name: ${name}`);
    };

    const authenticationManager = (): AuthenticationManager | undefined => {
        if (context.authenticationManager !== undefined) {
            return context.authenticationManager;
        }
        if (context.userDetailsService !== undefined) {
            const manager = userDetailsServiceAuthenticationManager(context.userDetailsService, {
                userDetailsPasswordService: context.userDetailsPasswordService
            });
            return manager;
        }
    }


    const middleware: WebFilter[] = [];

    class ServerHttpSecurity {
        #authenticationEntryPoint?: ServerAuthenticationEntryPoint;
        readonly #defaultEntryPoints: Array<[ServerWebExchangeMatcher, ServerAuthenticationEntryPoint]> = [];
        #authenticationManager?: AuthenticationManager;

        set authenticationManager(authenticationManager: AuthenticationManager | undefined) {
            this.#authenticationManager = authenticationManager;
        }

        get authenticationEntryPoint(): ServerAuthenticationEntryPoint | undefined {
            if (this.#authenticationEntryPoint !== undefined || this.#defaultEntryPoints.length === 0) {
                return this.#authenticationEntryPoint!;
            }
            if (this.#defaultEntryPoints.length === 1) {
                return this.#defaultEntryPoints[0][1];
            }
            return delegatingEntryPoint({
                entryPoints: this.#defaultEntryPoints,
                defaultEntryPoint: this.#defaultEntryPoints[this.#defaultEntryPoints.length - 1][1]
            });
        }


        build() {
            if (config.headers !== undefined && config.headers.disabled !== true) {
                const writer: WebFilter = headers(config.headers);
                writer[filterOrderSymbol] = filterOrder.http_headers;
                middleware.push(writer);
            }
            if (config.x509 !== undefined && config.x509.disabled !== true) {

                const filter: WebFilter = x509({
                    storage: context.storage,
                    getService,
                    extractor: subjectX500PrincipalExtractor({ principalAltName: config.x509.principalAltName })
                });
                filter[filterOrderSymbol] = filterOrder.authentication;
                middleware.push(filter);
            }
            if (config.cors?.disabled !== true && context.corsConfigSource !== undefined) {
                const filter: WebFilter = cors({corsConfigSource: context.corsConfigSource });
                filter[filterOrderSymbol] = filterOrder.cors;
                middleware.push(filter);
            }

            if (config.basic !== undefined && config.basic?.disabled !== true) {

                const defaultSuccessHandlers: ServerAuthenticationSuccessHandler[] = [
                    async ({exchange, chain}, _authentication) => {
                        return await chain.filter(exchange);
                    }
                ];

                const filter = httpBasic({
                    storage: context.storage,
                    manager: this.#authenticationManager!,
                    defaultEntryPoints: this.#defaultEntryPoints,
                    defaultSuccessHandlers,
                });
                filter[filterOrderSymbol] = filterOrder.http_basic;
                middleware.push(filter);
            }
            if (config.jwt !== undefined && config.jwt.disabled !== true) {

                const verifier = jwtVerifier({
                    issuerBaseUri: config.jwt.issuerUri,
                    issuer: config.jwt.issuer,
                    audience: config.jwt.audience
                });
                const decoder = async (token: string): Promise<Jwt> => {
                    try {
                        const {payload} = await verifier(token);
                        return {
                            tokenValue: token,
                            subject: payload.sub,
                            getClaimAsString(claim: string): string | undefined {
                                return payload[claim] as string | undefined;
                            }
                        };
                    } catch (e) {
                        if (e instanceof JwtVerifyError) {
                            throw new BadJwtError(e.message, {cause: e});
                        }
                        throw new JwtError("error occurred while attempting to decoding jwt", {cause: e});
                    }
                }


                const authenticationConverter = serverBearerTokenAuthenticationConverter({uriQueryParameter: true});
                const authenticationConverterMatcher: ServerWebExchangeMatcher = async (exchange) => {
                    try {
                        const a = await authenticationConverter(exchange)
                        if (a === undefined) {
                            return NO_MATCH;
                        }
                        return match();
                    }
                    catch (e) {
                        return NO_MATCH;
                    }
                }

                const entryPoint = bearerTokenServerAuthenticationEntryPoint({});

                this.#defaultEntryPoints.push([authenticationConverterMatcher, entryPoint]);

                const filter: WebFilter = resourceServer({
                    storage: context.storage,
                    entryPoint: entryPoint,
                    converter: authenticationConverter,
                    jwt: {decoder}});
                filter[filterOrderSymbol] = filterOrder.authentication;
                middleware.push(filter);

            }
            const exchangeF: WebFilter = exchangeFilter({storage: context.storage});
            middleware.push(exchangeF);
            exchangeF[filterOrderSymbol] = filterOrder.security_context_server_web_exchange;


            if (config.authorize !== undefined) {
                const errorFf: WebFilter = errorFilter({authenticationEntryPoint: this.authenticationEntryPoint});
                errorFf[filterOrderSymbol] = filterOrder.error_translation;
                middleware.push(errorFf)
                const buildAuthorizationManager = (authorize: HttpSecurityConfig['authorize']) => {
                    const mappings: ServerWebExchangeMatcherEntry<DefaultAuthorizationManager<{
                        exchange: ServerWebExchange
                    }>>[] = [];
                    let anyExchangeRegistered = false;
                    for (const [matcher, access] of authorize ?? []) {
                        let serverMatcher: ServerWebExchangeMatcher;
                        if (matcher === 'any-exchange') {
                            anyExchangeRegistered = true;
                            serverMatcher = anyExchange;
                        } else if (anyExchangeRegistered) {
                            throw new Error("Cannot register other matchers after 'any-exchange' matcher");
                        } else {
                            serverMatcher = matcher;
                        }
                        let manager: DefaultAuthorizationManager<{ exchange: ServerWebExchange }>;
                        if (access.access === 'permitted') {
                            manager = new DefaultAuthorizationManager(async () => new AuthorizationDecision(true));
                            manager.toString = () => 'AuthorizationManager[permitted]';
                        } else if (access.access === 'denied') {
                            manager = new DefaultAuthorizationManager(async () => new AuthorizationDecision(false));
                            manager.toString = () => 'AuthorizationManager[denied]';
                        } else if (access.access === 'authenticated') {
                            manager = new DefaultAuthorizationManager(async (p: Promise<Authentication | undefined>) => {
                                const authentication = await p;
                                if (authentication !== undefined) {
                                    return new AuthorizationDecision(authentication.authenticated);
                                }
                                return new AuthorizationDecision(false);
                            });
                            manager.toString = () => 'AuthorizationManager[authenticated]';
                        } else {
                            throw new Error(`Unknown access type: ${JSON.stringify(access)}`);
                        }
                        mappings.push([serverMatcher, manager]);
                    }
                    return delegatingAuthorizationManager({mappings});
                }
                const manager = buildAuthorizationManager(config.authorize);
                const filter: WebFilter = authorizationFilter({manager, storage: context.storage});
                filter[filterOrderSymbol] = filterOrder.authorization;
                middleware.push(filter);

            }

            middleware.sort((a, b) => {
                const aOrder = a[filterOrderSymbol] ?? filterOrder.last;
                const bOrder = b[filterOrderSymbol] ?? filterOrder.last;
                return aOrder - bOrder;
            });
        }
    }


    const security = new ServerHttpSecurity();
    security.authenticationManager = authenticationManager();
    security.build();


    return new MatcherSecurityWebFilterChain(anyExchange, ...middleware);
};
